import { useEffect, useState } from "react"
import { useTypedDispatch, useTypedSelector } from "../app/store"
import { checkSession } from "../features/auth/authThunks"

// Keys for session status in localStorage
// Note: We're just tracking the time, actual token is in httpOnly cookies
const SESSION_REFRESH_KEY = "session_last_refresh"
const SESSION_EXPIRY_BUFFER = 30 * 60 * 1000 // 30 minutes buffer before session expires
const SESSION_DURATION = 86400000 // 24 hours - matching server's session cookie maxAge

// Check if we're in a test session or team simulation context
// These use JWT tokens instead of session cookies
const isSpecialSessionContext = () => {
  return !!localStorage.getItem("testSessionToken") || !!localStorage.getItem("teamSimulationToken")
}

/**
 * Hook to check and maintain session status
 * @returns Object containing session check state
 */
export const useSessionCheck = () => {
  const dispatch = useTypedDispatch()
  const isAuthenticated = useTypedSelector(
    (state) => state.authSlice.isAuthenticated,
  )
  const [isCheckingSession, setIsCheckingSession] = useState(true)

  // Check if session needs refresh
  const isSessionRefreshNeeded = () => {
    // We track when the session was last refreshed
    // The actual session-id cookie lasts 24 hours
    const lastRefresh = parseInt(
      localStorage.getItem(SESSION_REFRESH_KEY) || "0",
      10,
    )
    const now = Date.now()

    // If session is older than (24h - buffer), refresh it
    const sessionAge = now - lastRefresh
    const refreshThreshold = SESSION_DURATION - SESSION_EXPIRY_BUFFER

    return sessionAge > refreshThreshold
  }

  // Record successful session refresh
  const markSessionRefreshed = () => {
    localStorage.setItem(SESSION_REFRESH_KEY, Date.now().toString())
  }

  // Check session on component mount
  useEffect(() => {
    const verifySession = async () => {
      try {
        setIsCheckingSession(true)

        // Skip session check for test sessions and team simulations
        // They use JWT tokens, not session cookies
        if (isSpecialSessionContext()) {
          setIsCheckingSession(false)
          return
        }

        // Check if we need to refresh based on last refresh time
        const needsRefresh = isSessionRefreshNeeded()

        if (needsRefresh || !isAuthenticated) {
          const result = await dispatch(checkSession())
          if (result) {
            markSessionRefreshed()
          }
        }
      } catch (error) {
        console.error("Session verification failed:", error)
      } finally {
        setIsCheckingSession(false)
      }
    }

    verifySession()

    // Setup automatic session check every 30 minutes
    // We'll only refresh if needed based on the timestamp
    const checkInterval = setInterval(
      () => {
        // Skip for test sessions and team simulations
        if (isSpecialSessionContext()) {
          return
        }
        if (isAuthenticated && isSessionRefreshNeeded()) {
          console.log("Session nearing expiration, refreshing...")
          // Need to manually handle Promise rather than using .then() on dispatch result
          const checkSessionPromise = dispatch(checkSession())
          checkSessionPromise.then((result: boolean) => {
            if (result) markSessionRefreshed()
          })
        }
      },
      30 * 60 * 1000, // Check every 30 minutes
    )

    return () => {
      clearInterval(checkInterval)
    }
  }, [dispatch, isAuthenticated])

  return {
    isCheckingSession,
    isAuthenticated,
  }
}
